import { useInfiniteQuery } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'

import { AGENT_URL } from '@/config/env'

export interface MeetingNotesQueryParams {
  limit?: number
  sort_by?: 'event_time' | 'created_at'
  sort_order?: 'asc' | 'desc'
  search?: string
}

export interface MeetingNoteRow {
  id: string
  title?: string | null
  summary?: string | null
  /** Whether a transcript exists to open. The text itself is PHI and is
   *  served only by /api/transcripts/{id}, behind transcript.detail. */
  has_transcript?: boolean
  event_time?: string | null
  duration_seconds?: number | null
  attendees?: string[] | null
  created_at?: string | null
}

interface MeetingNotesResponse {
  data: MeetingNoteRow[]
  pagination: {
    limit: number
    has_more: boolean
    next_cursor: string | null
  }
}

async function fetchMeetingNotes(
  token: string,
  params: MeetingNotesQueryParams,
  cursor?: string
): Promise<MeetingNotesResponse> {
  const qs = new URLSearchParams()

  if (params.limit) qs.set('limit', params.limit.toString())
  if (params.sort_by) qs.set('sort_by', params.sort_by)
  if (params.sort_order) qs.set('sort_order', params.sort_order)
  if (params.search) qs.set('search', params.search)
  if (cursor) qs.set('cursor', cursor)

  const response = await fetch(
    `${AGENT_URL}/data/meeting-notes?${qs.toString()}`,
    {
      headers: {
        Authorization: token,
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to fetch meeting notes: ${response.status}`)
  }

  return response.json()
}

export function useMeetingNotesTableQuery(
  params: MeetingNotesQueryParams = {}
) {
  const { getToken } = useAuth()

  return useInfiniteQuery<MeetingNotesResponse>({
    queryKey: ['meeting-notes', params],
    queryFn: async ({ pageParam }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('Missing auth token')
      }
      return fetchMeetingNotes(token, params, pageParam as string | undefined)
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination.next_cursor ?? undefined,
    initialPageParam: undefined,
  })
}
